import { z } from 'zod';
import { TENANT_SLUG_REGEX } from './platform-domain';

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'E-mail obrigatório')
    .email('E-mail inválido'),
  password: z
    .string()
    .min(1, 'Senha obrigatória')
    .min(6, 'Senha deve ter no mínimo 6 caracteres'),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Regras de senha (alinhadas com NIST SP 800-63B):
 * - mínimo 8 caracteres
 * - precisa ter ao menos 1 letra e 1 número
 * - máximo 64 caracteres (evita ataques de DoS via bcrypt)
 *
 * Bloqueamos senhas óbvias mais comuns para forçar o usuário a sair de
 * "12345678", "password" e variações antes de submeter.
 */
export const passwordSchema = z
  .string()
  .min(8, 'A senha precisa ter pelo menos 8 caracteres')
  .max(64, 'A senha não pode ter mais de 64 caracteres')
  .regex(/[A-Za-zÀ-ÿ]/, 'A senha precisa conter ao menos uma letra')
  .regex(/[0-9]/, 'A senha precisa conter ao menos um número')
  .refine(
    (v) => !/^(?:senha|password|12345678|qwerty|admin|barbearia)$/i.test(v),
    'Essa senha é fácil demais. Escolha uma combinação mais difícil de adivinhar.',
  );

/**
 * Schema de cadastro tradicional (email + senha).
 *
 * Mudancas vs versoes anteriores:
 * - `passwordConfirm` reintroduzido: wizard mobile 3-steps fica em passo
 *   dedicado a senha, e o template define "Senha" + "Confirmar senha"
 *   como expectativa do operador. Reduz risco de typo em fluxo de signup
 *   onde o cliente nao consegue testar a senha imediatamente (precisa
 *   confirmar email antes).
 * - Adicionado `slug` opcional: SlugPreview no form deixa o user editar
 *   o identificador (subdominio) antes de submeter.
 * - Adicionado `phone` opcional: produto gira em torno de WhatsApp e
 *   capturar ja no cadastro acelera onboarding do Evolution.
 */
export const registerSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Nome obrigatório')
      .min(2, 'Nome deve ter no mínimo 2 caracteres'),
    email: z
      .string()
      .min(1, 'E-mail obrigatório')
      .email('E-mail inválido'),
    password: passwordSchema,
    passwordConfirm: z.string().min(1, 'Confirme sua senha'),
    barbershopName: z
      .string()
      .min(1, 'Nome da barbearia obrigatório')
      .min(2, 'Nome deve ter no mínimo 2 caracteres'),
    slug: z
      .string()
      .max(60, 'Identificador muito longo')
      .optional(),
    phone: z
      .string()
      .max(20, 'Telefone muito longo')
      .optional(),
    acceptTerms: z.literal(true, {
      errorMap: () => ({
        message: 'Você precisa aceitar os termos para continuar',
      }),
    }),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: 'As senhas não coincidem',
    path: ['passwordConfirm'],
  });

export type RegisterInput = z.infer<typeof registerSchema>;

/**
 * Indicador visual de força da senha. Não é validação dura - só feedback ao
 * usuário durante a digitação.
 */
export function passwordStrength(password: string): {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
} {
  if (!password) return { score: 0, label: '' };
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Za-zÀ-ÿ]/.test(password) && /[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9À-ÿ]/.test(password)) score++;
  const labels = ['Muito fraca', 'Fraca', 'Razoável', 'Boa', 'Forte'] as const;
  return { score: Math.min(score, 4) as 0 | 1 | 2 | 3 | 4, label: labels[Math.min(score, 4)] };
}

/**
 * Schema do "esqueci minha senha" - apenas email.
 */
export const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'E-mail obrigatório').email('E-mail inválido'),
});
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;

/**
 * Schema do "redefinir senha" via token (link enviado por email).
 */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(32, 'Token inválido'),
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Confirme a senha'),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const serviceSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome obrigatório')
    .min(2, 'Nome deve ter no mínimo 2 caracteres'),
  description: z.string().optional(),
  duration: z
    .number({ required_error: 'Duração obrigatória' })
    .min(15, 'Duração mínima de 15 minutos'),
  price: z
    .number({ required_error: 'Preço obrigatório' })
    .min(0, 'Preço não pode ser negativo'),
  active: z.boolean().default(true),
  displayPhoto: z.boolean().default(false),
  /** Público do serviço (Agenda Personalizada). Unissex aparece em ambos os filtros. */
  audience: z.enum(['M', 'F', 'UX']).default('UX'),
  /** Profissionais que executam (Agenda Personalizada). Vazio = todos. */
  barberIds: z.array(z.string()).default([]),
});

export type ServiceInput = z.infer<typeof serviceSchema>;

export const productSchema = z
  .object({
    name: z.string().min(1, 'Nome obrigatório').min(2, 'Mínimo 2 caracteres'),
    sku: z.string().optional().or(z.literal('')),
    description: z.string().optional().or(z.literal('')),
    category: z.enum(['RESALE', 'INTERNAL', 'BOTH']).default('RESALE'),
    unit: z.string().min(1, 'Unidade obrigatória').default('un'),
    costPrice: z
      .number({ invalid_type_error: 'Valor inválido' })
      .min(0, 'Não pode ser negativo')
      .default(0),
    salePrice: z
      .number({ invalid_type_error: 'Valor inválido' })
      .min(0, 'Não pode ser negativo')
      .optional()
      .nullable(),
    minStock: z
      .number({ invalid_type_error: 'Valor inválido' })
      .min(0, 'Não pode ser negativo')
      .default(0),
    /** Quantidade ja em estoque ao cadastrar (so no create) - vira uma Entrada. */
    initialStock: z
      .number({ invalid_type_error: 'Informe um número válido' })
      .min(0, 'O estoque inicial não pode ser negativo')
      .optional(),
    active: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    if (
      (data.category === 'RESALE' || data.category === 'BOTH') &&
      (data.salePrice === undefined || data.salePrice === null)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['salePrice'],
        message: 'Produto de revenda precisa de preço de venda',
      });
    }
  });

export type ProductInput = z.infer<typeof productSchema>;

export const barberSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome obrigatório')
    .min(2, 'Nome deve ter no mínimo 2 caracteres'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  phone: z
    .string()
    .optional()
    .or(z.literal(''))
    .refine(
      (v) => !v || /^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(v),
      'Telefone inválido. Use o formato (DD) 9XXXX-XXXX',
    ),
  bio: z.string().optional(),
  specialties: z.array(z.string()).default([]),
  /** Serviços que o profissional executa (Agenda Personalizada). Vazio = todos. */
  serviceIds: z.array(z.string()).default([]),
  active: z.boolean().default(true),
  displayPhoto: z.boolean().default(false),
});

export type BarberInput = z.infer<typeof barberSchema>;

export const appointmentSchema = z.object({
  barberId: z.string().min(1, 'Barbeiro obrigatório'),
  serviceId: z.string().min(1, 'Serviço obrigatório'),
  customerName: z.string().min(1, 'Nome obrigatório'),
  customerPhone: z
    .string()
    .min(1, 'Telefone obrigatório')
    .regex(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, 'Telefone inválido'),
  customerEmail: z.string().email('E-mail inválido').optional().or(z.literal('')),
  scheduledAt: z.string().min(1, 'Data/hora obrigatória'),
  notes: z.string().optional(),
});

export type AppointmentInput = z.infer<typeof appointmentSchema>;

export const customerInfoSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome obrigatório')
    .min(2, 'Nome deve ter no mínimo 2 caracteres'),
  phone: z
    .string()
    .min(1, 'Telefone obrigatório')
    .regex(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, 'Telefone inválido'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
});

export type CustomerInfoInput = z.infer<typeof customerInfoSchema>;

export const customerProfileSchema = z.object({
  name: z
    .string()
    .min(1, 'Nome obrigatório')
    .min(2, 'Nome deve ter no mínimo 2 caracteres'),
  phone: z
    .string()
    .min(1, 'Telefone obrigatório')
    .regex(/^\(\d{2}\)\s\d{4,5}-\d{4}$/, 'Telefone inválido'),
  email: z.string().email('E-mail inválido').optional().or(z.literal('')),
  birthDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida')
    .optional()
    .or(z.literal('')),
  notes: z.string().max(2000, 'Máximo 2000 caracteres').optional().or(z.literal('')),
  marketingOptIn: z.boolean().default(false),
  showHistoryInApp: z.boolean().default(false),
});

export type CustomerProfileInput = z.infer<typeof customerProfileSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Senha atual obrigatória'),
    newPassword: z.string().min(6, 'Nova senha deve ter no mínimo 6 caracteres'),
    confirmPassword: z.string().min(1, 'Confirmação obrigatória'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Senhas não conferem',
    path: ['confirmPassword'],
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const tenantSchema = z.object({
  name: z.string().min(1, 'Nome obrigatório'),
  slug: z
    .string()
    .min(1, 'Slug obrigatório')
    .regex(
      TENANT_SLUG_REGEX,
      'Slug deve começar e terminar com letra/número, conter apenas letras minúsculas, números e hífens (max 63)',
    ),
  address: z.string().optional(),
  phone: z.string().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
});

export type TenantInput = z.infer<typeof tenantSchema>;

/**
 * Endereço estruturado da barbearia. Cada campo é opcional (o dono preenche o
 * que tiver). O backend compõe a linha única `Tenant.address` a partir daqui
 * (display para booking público/listagem/geocode).
 */
export const tenantAddressSchema = z.object({
  zip: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

export type TenantAddressInput = z.infer<typeof tenantAddressSchema>;

/**
 * Contato e localização editáveis pelo PRÓPRIO dono em /dashboard/settings.
 * NÃO inclui name/slug - administração de tenant é exclusiva do Super Admin
 * (perigoso delegar; quebra links). O dono só compartilha/usa o link.
 *
 * `phone` = celular/WhatsApp (vira instância Evolution, UNIQUE no backend).
 */
export const tenantContactSchema = z.object({
  address: tenantAddressSchema.optional(),
  phone: z.string().optional(),
  /** Telefone fixo da loja (opcional). Não é o WhatsApp - guardado para uso futuro. */
  landline: z.string().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
});

export type TenantContactInput = z.infer<typeof tenantContactSchema>;
